import React, { useState, useEffect } from "react";
import { sanitizeInput } from "./utils/sanitize";
import { useTranslation } from "./contexts/LocaleContext";
import { authenticatedFetch } from "./config/api";
import {
  Pipette,
  Trash2,
  AlertTriangle,
  User,
  MapPin,
  Mail,
  Phone,
  Hash,
  CreditCard,
  RefreshCw,
  Search,
} from "lucide-react";
import { Client } from "./types";

type ClientDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  clients: Client[];
  onAddClient: (client: Omit<Client, "id">) => void | Promise<void>;
  onUpdateClient: (
    id: string,
    client: Omit<Client, "id">
  ) => void | Promise<void>;
  onDeleteClient: (id: string) => void | Promise<void>;
  initialData?: Partial<Omit<Client, "id">>;
};

export default function ClientDialog({
  isOpen,
  onClose,
  clients,
  onAddClient,
  onUpdateClient,
  onDeleteClient,
  initialData,
}: ClientDialogProps) {
  const t = useTranslation();
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    email: "",
    phone: "",
    taxId: "",
    buyerId: "",
    isVatPayer: null as boolean | null,
  });

  // ARES states
  const [aresLoading, setAresLoading] = useState(false);
  const [aresStatus, setAresStatus] = useState<"idle" | "success" | "not_found" | "error">("idle");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredClients = clients.filter((client) => {
    const query = searchQuery.toLowerCase();
    return (
      client.name.toLowerCase().includes(query) ||
      (client.email && client.email.toLowerCase().includes(query)) ||
      (client.buyerId && client.buyerId.toLowerCase().includes(query))
    );
  });

  const isValidIco = (ico: string) => /^\d{8}$/.test(ico || "");

  const handleAresLookup = async () => {
    const ico = formData.buyerId || "";
    if (!isValidIco(ico)) {
      setAresStatus("error");
      return;
    }

    setAresLoading(true);
    setAresStatus("idle");

    try {
      const response = await authenticatedFetch(`/api/utils/ares/${ico}`);
      if (response.status === 404) {
        setAresStatus("not_found");
        return;
      }
      if (!response.ok) {
        throw new Error("ARES lookup failed");
      }

      const data = await response.json();

      setFormData((prev) => ({
        ...prev,
        name: data.obchodniJmeno || prev.name,
        address: data.sidlo?.textovaAdresa || prev.address,
        taxId: data.dic || prev.taxId,
      }));

      setAresStatus("success");
    } catch (error) {
      console.error("ARES lookup error:", error);
      setAresStatus("error");
    } finally {
      setAresLoading(false);
    }
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      // Pre-fill form with initialData if not editing
      if (!editingClient && initialData) {
        setFormData({
          name: initialData.name || "",
          address: initialData.address || "",
          email: initialData.email || "",
          phone: initialData.phone || "",
          taxId: initialData.taxId || "",
          buyerId: initialData.buyerId || "",
          isVatPayer: initialData.isVatPayer ?? null,
        });
      }
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, initialData, editingClient]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form before submission
    if (!isFormValid()) {
      return;
    }

    // Basic email validation
    if (formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        alert(t.landing.auth.errors.EMAIL_INVALID);
        return;
      }
    }

    try {
      if (editingClient) {
        await onUpdateClient(editingClient.id, formData);
      } else {
        await onAddClient(formData);
      }
      handleCancel();
    } catch (error) {
      console.error("Error submitting client form:", error);
      // Error handling is usually done in the parent via alerts,
      // but we should not close the dialog if it fails.
    }
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      address: client.address,
      email: client.email || "",
      phone: client.phone || "",
      taxId: client.taxId || "",
      buyerId: client.buyerId || "",
      isVatPayer: client.isVatPayer ?? null,
    });
  };

  const handleCancel = () => {
    setEditingClient(null);
    if (initialData) {
      setFormData({
        name: initialData.name || "",
        address: initialData.address || "",
        email: initialData.email || "",
        phone: initialData.phone || "",
        taxId: initialData.taxId || "",
        buyerId: initialData.buyerId || "",
        isVatPayer: initialData.isVatPayer ?? null,
      });
    } else {
      setFormData({
        name: "",
        address: "",
        email: "",
        phone: "",
        taxId: "",
        buyerId: "",
        isVatPayer: null,
      });
    }
  };

  const handleDelete = (id: string) => {
    if (
      window.confirm(
        t.clientDialog.deleteConfirm ||
          "Are you sure you want to delete this client?"
      )
    ) {
      onDeleteClient(id);
    }
  };

  // Validate form: at least one field must be filled
  const isFormValid = () => {
    return (
      formData.name.trim() !== "" ||
      formData.address.trim() !== "" ||
      formData.email.trim() !== "" ||
      formData.phone.trim() !== "" ||
      formData.taxId.trim() !== "" ||
      formData.buyerId.trim() !== "" ||
      formData.isVatPayer !== null
    );
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only close if click was directly on backdrop, not on child elements
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[200] p-4"
      onClick={handleBackdropClick}
      onMouseDown={(e) => {
        // Prevent closing when selecting text
        if (e.target !== e.currentTarget) {
          e.stopPropagation();
        }
      }}
    >
      <div
        className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl max-w-4xl w-full max-h-[calc(100vh-80px)] sm:max-h-[90vh] overflow-hidden border border-slate-200/50 dark:border-slate-800 transition-colors flex flex-col mt-[64px] sm:mt-0"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2 sm:p-6 border-b border-slate-100 dark:border-slate-800">
          <div>
            <h2 className="text-lg sm:text-xl font-black text-slate-900 dark:text-slate-100">
              {t.clientDialog.manageTitle || t.invoice.manageClients}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 sm:p-2 bg-slate-50 dark:bg-slate-800 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 rounded-xl transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-4 h-4 sm:w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-4 sm:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Left: Form */}
            <div className="space-y-6">
              <div>
                <h3 className="text-base font-black text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                  <div className="w-1.5 h-5 bg-indigo-600 rounded-full" />
                  {editingClient
                    ? t.clientDialog.editTitle
                    : t.clientDialog.addTitle}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-3">
                    <div className="relative">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 block">
                        {t.clientDialog.name}
                      </label>
                      <div className="relative">
                        <User className="absolute left-4 top-2.5 h-4 w-4 text-slate-400" />
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              name: sanitizeInput(e.target.value),
                            }))
                          }
                          placeholder={t.clientDialog.namePlaceholder}
                          className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all text-sm"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="sm:col-span-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 block">
                          {t.clientDialog.address}
                        </label>
                        <div className="relative">
                          <MapPin className="absolute left-4 top-2.5 h-4 w-4 text-slate-400" />
                          <textarea
                            value={formData.address}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                address: sanitizeInput(e.target.value),
                              }))
                            }
                            placeholder={t.clientDialog.addressPlaceholder}
                            rows={2}
                            className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all text-sm"
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-1">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 block">
                          {t.clientDialog.email}
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-4 top-2.5 h-4 w-4 text-slate-400" />
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                email: sanitizeInput(e.target.value),
                              }))
                            }
                            placeholder="email@example.com"
                            className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all text-sm"
                          />
                        </div>
                      </div>
                      <div className="sm:col-span-1">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 block">
                          {t.clientDialog.phone}
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-4 top-2.5 h-4 w-4 text-slate-400" />
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                phone: sanitizeInput(e.target.value),
                              }))
                            }
                            placeholder="+420..."
                            className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-800/10 space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                          {t.invoice.identificationNumber}
                        </label>
                        <div className="relative">
                          <Hash className="absolute left-3 top-2 h-4 w-4 text-slate-400" />
                          <input
                            type="text"
                            value={formData.buyerId}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                buyerId: sanitizeInput(e.target.value),
                              }))
                            }
                            placeholder="17653223"
                            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all text-xs"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleAresLookup}
                          className="w-full py-2 px-2 rounded-lg border border-purple-200 dark:border-purple-900/50 bg-purple-700 text-white text-[9px] font-black uppercase tracking-widest hover:bg-purple-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-1.5"
                          disabled={aresLoading}
                        >
                          {aresLoading ? (
                            <RefreshCw className="h-2.5 w-2.5 animate-spin" />
                          ) : null}
                          {aresLoading
                            ? t.invoice.validation.aresLoading
                            : t.invoice.validation.aresButton}
                        </button>
                        {aresStatus !== "idle" && (
                          <div
                            className={`text-[9px] font-medium px-1.5 py-0.5 rounded-md inline-block ${
                              aresStatus === "success"
                                ? "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20"
                                : aresStatus === "error" && !isValidIco(formData.buyerId)
                                ? "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20"
                                : "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20"
                            }`}
                          >
                            {aresStatus === "success" && t.common.success}
                            {aresStatus === "error" && !isValidIco(formData.buyerId) && t.invoice.validation.aresMinChars}
                            {aresStatus === "not_found" &&
                              t.invoice.validation.aresNotFound}
                            {aresStatus === "error" && isValidIco(formData.buyerId) &&
                              t.invoice.validation.aresError}
                          </div>
                        )}
                      </div>
                      <div className="space-y-1 pt-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 block">
                          {t.invoice.dic}
                        </label>
                        <div className="relative">
                          <CreditCard className="absolute left-4 top-2.5 h-4 w-4 text-slate-400" />
                          <input
                            type="text"
                            value={formData.taxId}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                taxId: sanitizeInput(e.target.value),
                              }))
                            }
                            placeholder="DIČ"
                            className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all text-sm"
                          />
                        </div>
                        <div className="pt-2">
                          <div className="flex flex-col gap-2 p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">
                              {t.invoice.vatPayer}
                            </span>
                            <select
                              value={
                                formData.isVatPayer === null
                                  ? ""
                                  : formData.isVatPayer.toString()
                              }
                              onChange={(e) => {
                                const val = e.target.value;
                                setFormData((prev) => ({
                                  ...prev,
                                  isVatPayer: val === "" ? null : val === "true",
                                }));
                              }}
                              className="w-full px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800 text-xs text-slate-900 dark:text-slate-100 iv-select"
                            >
                              <option value="">{t.invoice.notSpecified}</option>
                              <option value="true">
                                {t.invoice.vatPayer.replace("?", "")}
                              </option>
                              <option value="false">{t.invoice.nonVatPayer}</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {!isFormValid() && (
                    <div className="px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/30 rounded-xl flex items-center gap-2 text-amber-800 dark:text-amber-400 text-[10px] font-bold animate-pulse">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                      <span>{t.clientDialog.validationMessage}</span>
                    </div>
                  )}

                  <div className="flex gap-2 pt-2">
                    <button
                      type="submit"
                      disabled={!isFormValid()}
                      className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 dark:disabled:bg-slate-800 text-white rounded-xl font-black transition-all shadow-md shadow-indigo-200 dark:shadow-none text-sm"
                    >
                      {editingClient
                        ? t.clientDialog.update
                        : t.clientDialog.add}
                    </button>
                    {editingClient && (
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="px-4 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-xl font-black transition-all text-sm"
                      >
                        {t.common.cancel}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* Right: List */}
            <div className="space-y-4">
              <div className="flex flex-col gap-4">
                <h3 className="text-base font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <div className="w-1.5 h-5 bg-slate-200 dark:bg-slate-700 rounded-full" />
                  {t.clientDialog.existingClients}
                </h3>
                
                {/* Search Input */}
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t.common.search || "Search clients..."}
                    className="w-full pl-10 pr-4 py-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all text-sm"
                  />
                </div>
              </div>

              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                {filteredClients.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border-2 border-dashed border-slate-100 dark:border-slate-800">
                    <User className="w-10 h-10 text-slate-200 dark:text-slate-700 mx-auto mb-2" />
                    <p className="text-slate-400 dark:text-slate-500 italic text-xs font-medium">
                      {searchQuery ? "No matching clients found" : t.clientDialog.noClients}
                    </p>
                  </div>
                ) : (
                  filteredClients.map((client) => (
                    <div
                      key={client.id}
                      className={`group p-4 rounded-2xl border-2 transition-all ${
                        editingClient?.id === client.id
                          ? "border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20"
                          : "border-slate-50 dark:border-slate-800 hover:border-slate-200 dark:hover:border-slate-700"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 space-y-0.5">
                          <div className="flex items-center gap-2">
                            <p className="font-black text-sm text-slate-900 dark:text-slate-100 truncate">
                              {client.name}
                            </p>
                            {client.isVatPayer !== null &&
                              client.isVatPayer !== undefined && (
                                <span
                                  className={`text-[8px] font-black px-1 py-0.5 rounded uppercase tracking-tighter ${
                                    client.isVatPayer
                                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                                      : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                                  }`}
                                >
                                  {client.isVatPayer ? "VAT" : "NON-VAT"}
                                </span>
                              )}
                          </div>
                          <p className="text-[10px] text-slate-500 font-medium line-clamp-1">
                            {client.address}
                          </p>
                          {client.email && (
                            <div className="flex items-center gap-1 text-[9px] text-indigo-600 dark:text-indigo-400 font-bold">
                              <Mail className="w-2.5 h-2.5" />
                              {client.email}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity flex-shrink-0">
                          <button
                            onClick={() => handleEdit(client)}
                            className="p-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-100 transition-colors"
                            title={t.common.edit}
                          >
                            <Pipette className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDelete(client.id)}
                            className="p-2 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-100 transition-colors"
                            title={t.common.delete}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
